import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Globe, Plus, Loader2, Activity } from "lucide-react";
import { API, authHeaders } from "@/lib/site";
import { StatusDot } from "./ServersTab";

const inputCls =
  "px-4 py-3 rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary/60";

export default function SitesTab() {
  const [sites, setSites] = useState([]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [adding, setAdding] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/sites`, { headers: authHeaders() });
      setSites(data.sites);
    } catch {
      toast.error("Could not load sites.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await axios.post(`${API}/sites`, { name, url }, { headers: authHeaders() });
      toast.success("Site added — first health check is running now.");
      setName("");
      setUrl("");
      setTimeout(load, 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add site.");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div>
      <form onSubmit={add} className="rounded-3xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 sm:p-8 mb-8 flex flex-col sm:flex-row gap-4">
        <input required minLength={2} placeholder="Site name (e.g. Company Website)" value={name} onChange={(e) => setName(e.target.value)}
               className={`${inputCls} flex-1`} data-testid="site-add-name" />
        <input required minLength={4} placeholder="https://yourdomain.com" value={url} onChange={(e) => setUrl(e.target.value)}
               className={`${inputCls} flex-1`} data-testid="site-add-url" />
        <button type="submit" disabled={adding} data-testid="site-add-submit"
                className="inline-flex justify-center items-center gap-2 px-6 py-3 rounded-full bg-primary text-white font-semibold text-sm hover:bg-blue-700 disabled:opacity-60 transition-colors">
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Monitor Site
        </button>
      </form>

      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : sites.length === 0 ? (
        <div data-testid="sites-empty" className="rounded-3xl border border-dashed border-slate-300 dark:border-slate-700 p-16 text-center">
          <Globe className="w-10 h-10 mx-auto text-slate-300 dark:text-slate-600" />
          <p className="mt-4 font-semibold">No sites monitored yet</p>
          <p className="mt-2 text-sm text-slate-500">Add a site above and we'll health-check it automatically every 15 minutes.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="sites-list">
          {sites.map((s) => (
            <div key={s.site_id} data-testid={`site-row-${s.site_id}`}
                 className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 sm:p-6 flex flex-wrap items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="font-semibold truncate">{s.name}</p>
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">{s.url}</a>
              </div>
              <div className="flex items-center gap-6">
                {s.status === "unknown" ? (
                  <span className="text-xs text-slate-400 inline-flex items-center gap-1.5"><Activity className="w-3.5 h-3.5 animate-pulse" /> checking…</span>
                ) : (
                  <StatusDot status={s.status === "up" ? "running" : "offline"} />
                )}
                <span className="text-xs text-slate-500 font-mono">
                  {s.response_ms != null ? `${s.response_ms}ms` : "—"}
                </span>
                <span className="text-xs text-slate-400 hidden sm:inline">
                  {s.last_checked_at ? `checked ${new Date(s.last_checked_at).toLocaleTimeString()}` : "not checked yet"}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
